/** Underlyings the platform trades. SPXW is the weekly SPX option root. */
export const ADMIN_TRACKED_UNDERLYINGS = ['SPY', 'QQQ', 'SPX', 'SPXW'];
export const ADMIN_TRACKED_LABEL = 'SPY, QQQ, SPX';

const OCC_SYMBOL = /^([A-Z]+)\d{6}[CP]\d{8}$/;
const TRACKED = new Set(ADMIN_TRACKED_UNDERLYINGS);

export const underlyingOf = (symbol) => {
  if (!symbol) return null;
  const s = String(symbol).trim().toUpperCase();
  const m = s.match(OCC_SYMBOL);
  return m ? m[1] : s;
};

export const isTrackedSymbol = (symbol) => TRACKED.has(underlyingOf(symbol));

const round2 = (n) => Math.round(n * 100) / 100;

/** Categories that make up JTNC Trade P&L; mirrors functions-ts tradierHistoryService. */
const JTNC_CATEGORIES = new Set(['trade', 'expiration', 'subscription', 'credit']);

/**
 * Keeps fees/deposits/adjustments but drops fills and expirations on
 * underlyings outside the tracked list, then rebuilds the summary so the
 * totals match the rows shown.
 */
export function filterTradierHistoryToTracked(data) {
  if (!data?.events) return data;
  const events = data.events.filter(
    (e) =>
      (e.category !== 'trade' && e.category !== 'expiration') ||
      isTrackedSymbol(e.underlying || e.symbol)
  );

  const totals = {
    netDeposits: 0, jtncPnl: 0, tradingPnl: 0, subscriptions: 0, credits: 0,
    fees: 0, income: 0, other: 0, fills: 0, expirations: 0,
  };
  const months = new Map();
  for (const e of events) {
    const key = e.date.slice(0, 7);
    if (!months.has(key)) {
      months.set(key, {
        month: key, jtncPnl: 0, tradingPnl: 0, subscriptions: 0, credits: 0, fees: 0, deposits: 0, fills: 0, expirations: 0,
      });
    }
    const m = months.get(key);
    if (JTNC_CATEGORIES.has(e.category)) {
      totals.jtncPnl += e.amount; m.jtncPnl += e.amount;
    }
    switch (e.category) {
      case 'trade':
        totals.tradingPnl += e.amount; m.tradingPnl += e.amount;
        totals.fills += 1; m.fills += 1;
        break;
      case 'expiration':
        totals.tradingPnl += e.amount; m.tradingPnl += e.amount;
        totals.expirations += 1; m.expirations += 1;
        break;
      case 'subscription':
        totals.subscriptions += e.amount; m.subscriptions += e.amount;
        break;
      case 'credit':
        totals.credits += e.amount; m.credits += e.amount;
        break;
      case 'fee':
        totals.fees += e.amount; m.fees += e.amount;
        break;
      case 'deposit':
        totals.netDeposits += e.amount; m.deposits += e.amount;
        break;
      case 'income':
        totals.income += e.amount;
        break;
      default:
        totals.other += e.amount;
    }
  }

  const summary = {
    netDeposits: round2(totals.netDeposits),
    jtncPnl: round2(totals.jtncPnl),
    tradingPnl: round2(totals.tradingPnl),
    subscriptions: round2(totals.subscriptions),
    credits: round2(totals.credits),
    fees: round2(totals.fees),
    income: round2(totals.income),
    other: round2(totals.other),
    fills: totals.fills,
    expirations: totals.expirations,
    months: [...months.values()]
      .map((m) => ({
        ...m,
        jtncPnl: round2(m.jtncPnl),
        tradingPnl: round2(m.tradingPnl),
        subscriptions: round2(m.subscriptions),
        credits: round2(m.credits),
        fees: round2(m.fees),
        deposits: round2(m.deposits),
      }))
      .sort((a, b) => (a.month < b.month ? 1 : -1)),
  };

  return { ...data, events, summary, excludedEvents: data.events.length - events.length };
}
